import { Link } from "@tanstack/react-router";
import { MapPinOff, Search, Compass } from "lucide-react";

/**
 * Friendly empty/404 state for missing prayer locations.
 * Uses an animated SVG illustration tinted with the brand `prayer` color
 * (matching the rest of the religious vertical) — no extra image asset
 * required, so it renders instantly even for SSR / 404 responses.
 */
export function EmptyLocationState({
  variant = "sholat",
  title,
  message,
}: {
  variant?: "sholat" | "imsak" | "buka-puasa";
  title?: string;
  message?: string;
}) {
  const heading = title ?? "Lokasi tidak ditemukan";
  const desc =
    message ??
    "Maaf, lokasi yang Anda buka belum tersedia di indeks kami. Coba cari kota atau kecamatan terdekat di bawah.";
  const basePath = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;
  const alternatePath = variant === "sholat" ? "/jadwal-imsak/lokasi" : "/jadwal-sholat/lokasi";

  return (
    <section className="mx-auto flex w-full max-w-xl flex-col items-center px-4 py-12 text-center">
      <div className="relative mb-6 h-40 w-40">
        {/* Soft halo */}
        <div className="absolute inset-0 animate-pulse rounded-full bg-prayer/15 blur-xl" />
        {/* Mascot */}
        <svg
          viewBox="0 0 200 200"
          className="relative h-full w-full drop-shadow-md"
          aria-hidden
        >
          {/* Ground */}
          <ellipse cx="100" cy="172" rx="70" ry="6" fill="currentColor" className="text-prayer/15" />
          {/* Compass body */}
          <g style={{ animation: "lov-spin 8s linear infinite", transformBox: "fill-box", transformOrigin: "100px 110px" }}>
            <circle cx="100" cy="110" r="46" fill="currentColor" className="text-prayer" />
            <circle cx="100" cy="110" r="46" fill="none" stroke="currentColor" className="text-prayer-foreground/40" strokeWidth="2" />
            {/* Needle */}
            <polygon points="100,72 106,110 100,148 94,110" fill="currentColor" className="text-prayer-foreground" />
            <circle cx="100" cy="110" r="4" fill="currentColor" className="text-prayer-foreground" />
          </g>
          {/* Crescent on top */}
          <g style={{ animation: "lov-bob 3.2s ease-in-out infinite" }}>
            <path
              d="M100 30c-12 0-22 9-22 21 0 11 9 20 20 20 4 0 8-1 11-3-9-1-16-9-16-19s7-18 16-19c-3-2-6-3-9-3z"
              fill="currentColor"
              className="text-prayer"
            />
          </g>
          {/* Floating dots */}
          <g className="text-prayer/60" fill="currentColor">
            <circle cx="40" cy="50" r="3" style={{ animation: "lov-bob 2.4s ease-in-out infinite" }} />
            <circle cx="160" cy="60" r="2" style={{ animation: "lov-bob 3s ease-in-out infinite reverse" }} />
            <circle cx="170" cy="130" r="3" style={{ animation: "lov-bob 2.7s ease-in-out infinite" }} />
            <circle cx="32" cy="140" r="2" style={{ animation: "lov-bob 3.4s ease-in-out infinite reverse" }} />
          </g>
        </svg>
        <style>{`
          @keyframes lov-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes lov-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        `}</style>
      </div>

      <h1 className="font-display text-2xl tracking-tight md:text-3xl">
        <MapPinOff className="mr-2 inline h-6 w-6 text-prayer" />
        {heading}
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{desc}</p>

      <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm">
        <Link
          to={`${basePath}/lokasi` as never}
          className="inline-flex items-center gap-1.5 rounded-md bg-prayer px-4 py-2 font-medium text-prayer-foreground hover:bg-prayer/90"
        >
          <Search className="h-4 w-4" /> Cari lokasi lain
        </Link>
        <Link
          to={`${basePath}/dki-jakarta` as never}
          className="inline-flex items-center gap-1.5 rounded-md border border-prayer/40 px-4 py-2 font-medium text-prayer hover:bg-prayer/10"
        >
          <Compass className="h-4 w-4" /> Jakarta (default)
        </Link>
        <Link
          to={alternatePath as never}
          className="inline-flex items-center rounded-md border border-border px-4 py-2 hover:bg-accent"
        >
          {variant === "sholat" ? "Lihat Imsakiyah" : "Lihat Jadwal Sholat"}
        </Link>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        Dataset bertahap: 38 provinsi · 84+ kota · 350+ kecamatan. Kecamatan baru
        kami tambahkan secara berkala.
      </p>
    </section>
  );
}
